import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@digitalstreams/saas-core/vue/stores/authStore";

// Auth screens. LoginForm + RegisterForm are wrapped locally so we can
// handle the @success emit with a router redirect — saas-core's components
// are intentionally hands-off about routing. ForgotPassword / ResetPassword /
// VerifyEmail show their own confirmation UI and don't need a redirect hook.
import Login from "../views/Login.vue";
import Register from "../views/Register.vue";
import ForgotPassword from "@digitalstreams/saas-core/vue/views/ForgotPassword.vue";
import ResetPassword from "@digitalstreams/saas-core/vue/views/ResetPassword.vue";
import VerifyEmail from "@digitalstreams/saas-core/vue/views/VerifyEmail.vue";
// Account onboarding for admin-provisioned users (alpha.12) — the account-setup
// email links to /onboard?token=…; MUST be an explicit route or the catch-all
// swallows it to /quizzes → /login (the exact bug alpha.12 fixes).
import Onboard from "@digitalstreams/saas-core/vue/views/Onboard.vue";

// Shells — one per layout. AdminShell wraps the local AdminLayout, ConsumerShell
// wraps the local ConsumerLayout. Both are thin: they pass app-specific menu
// config to their layout and embed a <router-view /> for the child route.
import AdminShell from "../AdminShell.vue";
import ConsumerShell from "../views/ConsumerShell.vue";
// PublicLayout is the SIGNED-OUT storefront shell (no sidebar, no app chrome).
// AutoShell picks between it and ConsumerShell for routes both audiences reach.
import PublicLayout from "../views/public/PublicLayout.vue";
import PublicHome from "../views/public/PublicHome.vue";
import PublicPage from "../views/public/PublicPage.vue";
import PublicDeviceCheck from "../views/public/PublicDeviceCheck.vue";
import AutoShell from "../views/AutoShell.vue";

// Admin screens (platform-admin surface)
import AdminPlatformDashboard from "@digitalstreams/saas-core/vue/admin/AdminPlatformDashboard.vue";
import AdminTenants from "@digitalstreams/saas-core/vue/admin/AdminTenants.vue";
import AdminUsersPage from "../views/AdminUsersPage.vue";
import AdminRoles from "@digitalstreams/saas-core/vue/admin/AdminRoles.vue";
import AdminPlatformAuditLog from "@digitalstreams/saas-core/vue/admin/AdminPlatformAuditLog.vue";
import AdminEmailConfig from "@digitalstreams/saas-core/vue/admin/AdminEmailConfig.vue";
import AdminEmailLogs from "@digitalstreams/saas-core/vue/admin/AdminEmailLogs.vue";
import AdminEventDispatch from "@digitalstreams/saas-core/vue/admin/AdminEventDispatch.vue";
import AdminPublicSignup from "@digitalstreams/saas-core/vue/admin/AdminPublicSignup.vue";
// Platform-wide account lifecycle (alpha.13) — search + suspend / reactivate /
// soft-delete of any account. Distinct from AdminUsersPage (tenant user mgmt).
import AdminPlatformUsers from "@digitalstreams/saas-core/vue/admin/AdminPlatformUsers.vue";
// Email Templates manager (alpha.15) — flat, editable list of every system
// email with preview / test-send / reset, over the Event Dispatch data.
import AdminEmailTemplates from "@digitalstreams/saas-core/vue/admin/AdminEmailTemplates.vue";

// Phase 1 Settings shell (alpha.6) — tabbed config screen hosting the four
// already-shipped config components plus the new AdminGeneralConfig.
import AdminSettings from "@digitalstreams/saas-core/vue/admin/AdminSettings.vue";
import AdminSubscriptionPlans from "@digitalstreams/saas-core/vue/admin/AdminSubscriptionPlans.vue";
import AdminStripeConfig from "@digitalstreams/saas-core/vue/admin/AdminStripeConfig.vue";
import AdminGeneralConfig from "@digitalstreams/saas-core/vue/admin/AdminGeneralConfig.vue";
// AI Configuration — i-Recall's role→service assignment + provider connection
// checks (role mapping is i-Recall-specific; saas-core's page has none).
import AdminAiConfig from "../views/admin/AdminAiConfig.vue";

// Consumer screens (user-owned surface)
import HomeDashboard from "../views/HomeDashboard.vue";
import Profile from "../views/Profile.vue";
import QuizzesShell from "../views/quizzes/QuizzesShell.vue";
import QuizzesDecks from "../views/quizzes/QuizzesDecks.vue";
import QuizzesMap from "../views/quizzes/QuizzesMap.vue";
import QuizzesHistory from "../views/quizzes/QuizzesHistory.vue";
import QuizzesStats from "../views/quizzes/QuizzesStats.vue";
import QuizzesPlay from "../views/quizzes/QuizzesPlay.vue";
// My Library — DB-backed authored decks (create / detail / generate).
import LibraryDecks from "../views/library/LibraryDecks.vue";
import LibraryDeckDetail from "../views/library/LibraryDeckDetail.vue";
import LibraryDeckEdit from "../views/library/LibraryDeckEdit.vue";
import LibraryImport from "../views/library/LibraryImport.vue";
import LibraryImportBatch from "../views/library/LibraryImportBatch.vue";
import SettingsShell from "../views/settings/SettingsShell.vue";
import SettingsDevice from "../views/settings/SettingsDevice.vue";
import SettingsPreferences from "../views/settings/SettingsPreferences.vue";
import SettingsNotifications from "../views/settings/SettingsNotifications.vue";
import SettingsLibrary from "../views/settings/SettingsLibrary.vue";
// Tenant-admin settings (organization + branding) — saas-core surface, new 2026-06-11
import TenantSettings from "@digitalstreams/saas-core/vue/admin/TenantSettings.vue";
// Tenant-admin team management — alpha.10
import TeamPage from "../views/TeamPage.vue";
// Tenant-admin per-project membership (saas-core v1.3.x AdminProjects)
import ProjectsPage from "../views/ProjectsPage.vue";

const APP_NAME = "i-Recall";

// Consumer-shell routes are declared with EXPLICIT parent paths (`/profile`,
// `/quizzes`) rather than sharing `/` with AdminShell. Two parents at the same
// path force vue-router to pick by registration order, which is fragile.
// Distinct parents means each URL has exactly one possible shell.
const routes = [
  // ---- PUBLIC storefront (signed-out shell) --------------------------------
  // The bare root IS the public deck catalogue — rendered, not redirected, so
  // the marketing URL stays app.i-recall.au. A signed-in visitor is forwarded to
  // /home by evaluateRouteGuard, so this shell only ever serves anonymous users.
  {
    path: "/",
    component: PublicLayout,
    children: [
      { path: "", component: PublicHome },
      // Signed-out device check — the "Test microphone" button on PublicHome
      // lands here instead of the auth-gated /settings, so a casual visitor can
      // confirm mic + speakers before signing up. Reuses SettingsDevice.
      { path: "device-check", component: PublicDeviceCheck },
      // Static pages. Copy lives in views/public/publicPages.js; `meta.page`
      // selects which entry PublicPage renders.
      { path: "how-it-works", component: PublicPage, meta: { page: "how-it-works" } },
      { path: "pricing",      component: PublicPage, meta: { page: "pricing" } },
      { path: "about",        component: PublicPage, meta: { page: "about" } },
      { path: "contact",      component: PublicPage, meta: { page: "contact" } },
      { path: "privacy",      component: PublicPage, meta: { page: "privacy" } },
      { path: "terms",        component: PublicPage, meta: { page: "terms" } }
    ]
  },

  // ---- Guest screens (no shell) ----
  { path: "/login", component: Login, meta: { guestOnly: true } },
  { path: "/register", component: Register, meta: { guestOnly: true } },
  {
    path: "/forgot-password",
    component: ForgotPassword,
    props: { appName: APP_NAME },
    meta: { guestOnly: true }
  },
  {
    path: "/reset-password",
    component: ResetPassword,
    props: { appName: APP_NAME },
    meta: { guestOnly: true }
  },
  {
    path: "/verify-email",
    component: VerifyEmail,
    props: { appName: APP_NAME }
  },
  {
    // Account-setup landing for admin-provisioned users (alpha.12). The email
    // button is /onboard?token=…; Onboard reads the token from the query.
    path: "/onboard",
    component: Onboard,
    props: { appName: APP_NAME, successRoute: "/home", loginRoute: "/login" },
    meta: { guestOnly: true }
  },

  // ---- Consumer shell (ConsumerLayout skin) — declared BEFORE the admin
  // catch-all so its specific paths match first. ----
  {
    // Home dashboard — the default landing page for a signed-in learner.
    // Orients + re-engages (greet, resume, at-a-glance stats, recent activity,
    // jump-back-in) over the existing /api/me/* + /api/decks contracts.
    path: "/home",
    component: ConsumerShell,
    // Soft-private: signed-in users get the dashboard; anonymous visitors fall
    // through to the public /topics demo (see evaluateRouteGuard).
    meta: { requiresAuth: true, publicFallback: true },
    children: [
      { path: "", component: HomeDashboard }
    ]
  },
  {
    path: "/profile",
    component: ConsumerShell,
    meta: { requiresAuth: true },
    children: [
      { path: "", component: Profile }
    ]
  },
  {
    // "Topics" — the voice-practice surface. Reachable by BOTH audiences, so the
    // shell is chosen by audience (AutoShell): a signed-in learner gets the app
    // chrome; an anonymous visitor gets the public storefront chrome, so
    // clicking Practise on the public page doesn't drop them into a sidebar app.
    //
    // The tabbed browser at /topics itself is the SIGNED-IN picker (drill-down +
    // History/Stats). An anonymous visitor is sent to "/" — the storefront — by
    // the guard; the player and Map stay open to them.
    path: "/topics",
    component: AutoShell,
    children: [
      {
        path: "",
        component: QuizzesShell,
        children: [
          // signedInBrowse sits on the LEAF, not the shell: route meta is
          // inherited by children, so putting it on QuizzesShell would also
          // bounce an anonymous visitor off the Map — which is public.
          { path: "",        component: QuizzesDecks, meta: { signedInBrowse: true } },
          { path: "map",     component: QuizzesMap },
          { path: "history", component: QuizzesHistory, meta: { requiresAuth: true } },
          { path: "stats",   component: QuizzesStats,   meta: { requiresAuth: true } }
        ]
      },
      // Focused player — sibling of the tabbed shell so it renders full-bleed
      // (no PageTabs). hideFeatures keeps the public value-prop strip off a live
      // quiz (it's page furniture, not app furniture).
      { path: "play/:slug", component: QuizzesPlay, meta: { hideFeatures: true } }
    ]
  },
  {
    // My Library — DB-backed authored decks. `/new` is declared before `/:id`
    // so the static path matches first.
    path: "/library",
    component: ConsumerShell,
    meta: { requiresAuth: true },
    children: [
      { path: "",         component: LibraryDecks },
      { path: "new",          component: LibraryDeckEdit },
      { path: "import",       component: LibraryImportBatch }, // unified staged importer
      { path: "import-legacy", component: LibraryImport },     // old single-deck 4-option importer (kept, unadvertised)
      { path: ":id",      component: LibraryDeckDetail },
      { path: ":id/edit", component: LibraryDeckEdit }
    ]
  },
  {
    // Super-admin curation of the PUBLIC/homepage catalog. Same import machinery
    // as /library/import but always seeds to the public catalog (curate:true) and
    // is gated by requiresAdmin. Distinct URL + nav item so it reads as special.
    path: "/curate",
    component: ConsumerShell,
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: "", component: LibraryImportBatch, meta: { curate: true } }
    ]
  },
  {
    // Tenant-admin team management. No requiresAdmin — the API itself
    // requires a tenant-admin role and scopes everything to the caller's
    // tenant; platform admins see it via /ds-ops/users instead.
    path: "/team",
    component: ConsumerShell,
    meta: { requiresAuth: true },
    children: [
      { path: "", component: TeamPage }
    ]
  },
  {
    // Tenant-admin per-project membership (saas-core v1.3.x). Like /team: no
    // requiresAdmin — the API requires a tenant-admin role and scopes to the
    // caller's tenant.
    path: "/projects",
    component: ConsumerShell,
    meta: { requiresAuth: true },
    children: [
      { path: "", component: ProjectsPage }
    ]
  },
  {
    path: "/settings",
    component: ConsumerShell,
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        component: SettingsShell,
        children: [
          { path: "",              component: SettingsDevice },
          { path: "preferences",   component: SettingsPreferences },
          { path: "notifications", component: SettingsNotifications },
          { path: "library",       component: SettingsLibrary },
          { path: "organization",  component: TenantSettings }
        ]
      }
    ]
  },

  // ---- Admin shell (AdminLayout skin) — now at `/ds-ops/*` so the
  // admin console is reachable only by knowing the URL. The Vue SPA's
  // root (`/admin/` URL) renders the consumer surface for everyone, admin
  // or not. Platform-admin nav lives here for discoverability for admins
  // already in the surface.
  //
  // URL examples (with SPA base `/admin/`):
  //   /admin/ds-ops/             → AdminPlatformDashboard
  //   /admin/ds-ops/tenants      → AdminTenants
  //   /admin/ds-ops/users        → AdminUsersPage
  {
    path: "/ds-ops",
    component: AdminShell,
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: "",              component: AdminPlatformDashboard },
      { path: "tenants",       component: AdminTenants },
      { path: "email-config",  component: AdminEmailConfig },
      { path: "signup-config", component: AdminPublicSignup },
      { path: "email-logs",    component: AdminEmailLogs },
      { path: "email-templates", component: AdminEmailTemplates },
      { path: "events",        component: AdminEventDispatch },
      { path: "users",          component: AdminUsersPage },
      { path: "platform-users", component: AdminPlatformUsers },
      { path: "roles",          component: AdminRoles },
      { path: "ai-config",      component: AdminAiConfig },
      { path: "audit",          component: AdminPlatformAuditLog },
      // Settings shell (alpha.6) — tabbed page bundling the five config
      // sections behind /ds-ops/settings/{plans,stripe,email,signup,general}.
      // basePath prop tells AdminSettings how to compose absolute tab URLs.
      {
        path: "settings",
        component: AdminSettings,
        props: { basePath: "/ds-ops/settings" },
        children: [
          // Absolute redirect — a relative `redirect: "plans"` resolves against
          // the PARENT (/ds-ops/plans, unregistered) and falls through to
          // the catch-all → /quizzes. Bug found in browser 2026-06-11.
          { path: "",        redirect: "/ds-ops/settings/plans" },
          { path: "plans",   component: AdminSubscriptionPlans },
          { path: "stripe",  component: AdminStripeConfig },
          { path: "email",   component: AdminEmailConfig },
          { path: "signup",  component: AdminPublicSignup },
          { path: "general", component: AdminGeneralConfig }
        ]
      }
    ]
  },

  // NOTE: "/" is NOT declared here — it's an `alias` on the /topics route above,
  // so the bare URL RENDERS the public deck browser instead of redirecting to it.
  // (A signed-in visitor is forwarded to /home by evaluateRouteGuard.)

  // Catch-all: any unmatched path routes to the home dashboard rather than
  // rendering blank or leaking admin. An anonymous visitor falls through from
  // /home to "/" (publicFallback), so a bad URL still lands on the public page.
  { path: "/:pathMatch(.*)*", redirect: "/home" }
];

const router = createRouter({
  // SPA mounts at the application root. Express serves the SPA build's
  // index.html for any non-API, non-static-asset path; vue-router takes
  // over from there. Consumer routes (/quizzes, /profile, /settings) and
  // the admin section (/ds-ops/*) all resolve here. The vanilla
  // voice loop lives at /voice (Express-served voice.html), independent.
  history: createWebHistory("/"),
  routes
});

// Per-route guard. The three rules (requiresAuth / requiresAdmin / guestOnly)
// are implemented as the pure function `evaluateRouteGuard(to, auth)` in
// guard.js so they can be unit-tested without mounting a router. See that
// file's JSDoc for the rule semantics.
import { evaluateRouteGuard } from "./guard.js";

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.initialized) await auth.init();
  return evaluateRouteGuard(to, auth);
});

export default router;
